import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, AlertCircle, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDashboardFiltersStore } from '@/store/dashboardFiltersStore';
import type { BrowserData } from '../api/dashboard.schemas';

interface BrowserChartProps {
  data?: BrowserData;
  loading?: boolean;
  error?: Error;
}

/**
 * BrowserChart Component
 * 
 * Displays a chart showing the most used browsers for verification.
 * Helps understand user browser preferences for technical optimization.
 */
export function BrowserChart({
  data,
  loading = false,
  error
}: BrowserChartProps) {
  const { t } = useTranslation();
  const activeBrowser = useDashboardFiltersStore((s) => s.filters.browser);
  const toggleFilter = useDashboardFiltersStore((s) => s.toggleFilter);
  
  // Loading state
  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.browsersUsed')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.browserDistribution')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
            {t('dashboard.loadingChart')}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.chartError')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.failedToLoadBrowserData')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">{t('dashboard.unableToDisplayChart')}</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data || data.data.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.browsersUsed')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.browserDistribution')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Globe className="h-6 w-6 mx-auto mb-2" />
            <p>{t('dashboard.noBrowserData')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort data by value (highest to lowest) and calculate total
  const sortedData = [...data.data].sort((a, b) => b.value - a.value);
  const total = data.data.reduce((sum, item) => sum + item.value, 0);
  const colors = [
    'bg-orange-500',
    'bg-blue-500',
    'bg-green-500', 
    'bg-red-500',
    'bg-purple-500',
    'bg-teal-500',
    'bg-pink-500',
    'bg-indigo-500'
  ];

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">{t('dashboard.browsersUsed')}</CardTitle>
        </div>
        <CardDescription>
          {t('dashboard.browserDistributionWithTotal', { total })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-3 overflow-y-auto">
          {sortedData.map((item, index) => {
            const percentage = total > 0 ? (item.value / total) * 100 : 0;
            const colorClass = colors[index % colors.length];
            const isActive = activeBrowser === item.name;

            return (
              <button
                type="button"
                key={index}
                onClick={() => toggleFilter('browser', item.name)}
                className={`w-full text-left flex items-center justify-between p-3 rounded-lg border transition-colors hover:bg-muted/40 ${
                  isActive ? 'border-primary ring-1 ring-primary/40 bg-primary/5' : ''
                }`}
                aria-pressed={isActive}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${colorClass}`} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {percentage.toFixed(1)}% {t('dashboard.ofTotal')}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="font-semibold">{item.value}</p>
                  <p className="text-xs text-muted-foreground">{t('dashboard.uses')}</p>
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}